import { Icons } from '@bob/library'
import { CombinationOfMtmParams } from '@websrc/App/Hooks/useMTMParams'
import { BuildLocale } from '@websrc/Build/Pages/buildTypes'
import { CSCta } from '@websrc/Typings/ContentStack'
import { match, P } from 'ts-pattern'

import { exists, isDefined } from './Helpers/tsPattern'
import { mapAsset } from './mapAsset'

export enum CTAType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
}

export interface MapCTAProps {
  __ctaType: 'internal' | 'external' | 'file' | 'modal' | 'button'
  url?: string | null
  internal_link_section?: string
  file?: {
    title: string
    url: string
  } | null
  text: string
  newTab: boolean
  type?: CTAType
  icon?: keyof typeof Icons
  combination_of_mtm_params?: CombinationOfMtmParams
  mtm_params?: object | null | undefined
  modal_id?: number | null | undefined
  trackingId?: string | null | undefined
  ignorePopup?: boolean
}

export const mapCTA = (cta: CSCta, locale: BuildLocale): MapCTAProps | null => {
  return match(cta)
    .with(
      { internal_link: P.when((ref) => Boolean(ref?.length)) },
      ({
        internal_link,
        internal_link_section = '',
        cta_text,
        open_in_new_tab,
        type,
        icon,
        combination_of_mtm_params,
        mtm_params,
        tracking_id,
      }) => ({
        __ctaType: 'internal' as const,
        url: internal_link?.map((link) => {
          if (!link) return undefined
          const { url } = link as { url?: string }
          if (!url) return undefined
          if ('lesson_plan_steps' in link) return `/lessons${url}`
          if ('lessons' in link) return `/lessons${url}`
          if ('product_info' in link) return `/products${url}`
          if ('page_details' in link) return `/shop${url}`
          if ('tutorial' in link) return url?.startsWith(`/start/`) ? url : `/start${url}`
          if ('sections' in link) return `/product-resources${url}`
          return url
        })[0],
        internal_link_section: internal_link_section ?? '',
        text: `${cta_text}`,
        newTab: open_in_new_tab,
        type: type?.toLowerCase() as CTAType,
        icon: icon === 'NONE' ? undefined : (icon?.replace(' ', '_') as keyof typeof Icons),
        combination_of_mtm_params: combination_of_mtm_params
          ? combination_of_mtm_params
          : undefined,
        mtm_params: mtm_params ? mtm_params : undefined,
        trackingId: tracking_id ? tracking_id : undefined,
      })
    )
    .with(
      { external_link: exists },
      ({
        external_link,
        cta_text,
        open_in_new_tab,
        type,
        icon,
        combination_of_mtm_params,
        mtm_params,
        tracking_id,
        ignore_popup,
      }) => ({
        __ctaType: 'external' as const,
        url: external_link,
        text: `${cta_text}`,
        newTab: open_in_new_tab,
        type: type?.toLowerCase() as CTAType,
        icon: icon === 'NONE' ? undefined : (icon?.replace(' ', '_') as keyof typeof Icons),
        combination_of_mtm_params: combination_of_mtm_params
          ? combination_of_mtm_params
          : undefined,
        internal_link_section: '',
        mtm_params: mtm_params ? mtm_params : undefined,
        trackingId: tracking_id ? tracking_id : undefined,
        ignorePopup: ignore_popup ?? false,
      })
    )
    .with({ modal_id: exists }, ({ modal_id, cta_text, tracking_id }) => ({
      __ctaType: 'modal' as const,
      text: `${cta_text}`,
      newTab: false,
      internal_link_section: '',
      modal_id: modal_id,
      trackingId: tracking_id ? tracking_id : undefined,
    }))
    .with(
      { file_link: isDefined },
      ({ file_link, cta_text, open_in_new_tab, type, icon, tracking_id }) => ({
        __ctaType: 'file' as const,
        file: file_link && mapAsset(file_link, locale),
        text: `${cta_text}`,
        newTab: open_in_new_tab,
        internal_link_section: '',
        type: type?.toLowerCase() as CTAType,
        icon: icon === 'NONE' ? undefined : (icon?.replace(' ', '_') as keyof typeof Icons),
        trackingId: tracking_id ? tracking_id : undefined,
      })
    )
    .otherwise(() => null)
}
